import { Autocomplete } from 'components/Autocomplete'
import { Button } from 'components/Button'
import { Checkbox } from 'components/Checkbox'
import { Link } from 'components/Link'
import { RadioGroup } from 'components/RadioGroup'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { TextInput } from 'components/TextInput'
import { differenceInMonths, format } from 'date-fns'
import {
  PetAttempt,
  PetAttemptStep2,
  useSubmitPetAttempt,
  determineFeedingProductType,
} from 'features/MealPlan/api/mutations/useSubmitPetAttempt'
import { useFetchPetAttempt } from 'features/MealPlan/api/queries/useFetchPetAttempt'
import { useFetchPetBreeds } from 'features/MealPlan/api/queries/useFetchPetBreeds'
import { InputLabel } from 'features/MealPlan/components/InputLabel'
import { MealPlanLayout } from 'features/MealPlan/components/MealPlanLayout'
import {
  MealPlanQuestionnaireCard,
  MealPlanFormContainer,
} from 'features/MealPlan/components/MealPlanLayout/MealPlanLayout.styles'
import { RadioOptionsWrapper } from 'features/MealPlan/components/RadioOptionsWrapper'
import { Loader } from 'features/components/Loader'
import { useRouter } from 'hooks/useRouter'
import { useTranslation } from 'hooks/useTranslation'
import {
  ActivityLevels,
  BodyConditions,
  DietPrior,
  PetAdultSizes,
  PetWeightChangeDesire,
  Proteins,
} from 'models/Pet'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { capitalize } from 'utils/capitalize'
import { ozToLb } from 'utils/conversions'
import { lbToOz } from 'utils/weight'
import { TextRadioOption } from '../Step1/components/TextRadioOption'
import { CTAContainer, RadioGroupContainer, Step2Root, LoseGainContainer } from './Step2.styles'
import { useToast } from 'contexts/ToastContext'
import { FooterMessage } from 'features/MealPlan/components/FooterMessage'
import happyDog from '../../../../../public/assets/icons/happy-dog.svg'

type Step2Props = {}

export const Step2 = (_props: Step2Props) => {
  const { t } = useTranslation()

  const { addToast, removeToast } = useToast()

  const router = useRouter()

  const { data: petAttemptStep2, status } = useFetchPetAttempt({ retry: false })
  const { data: petBreeds = [] } = useFetchPetBreeds({
    retry: false,
    onSuccess: (breeds) => {
      setBreeds(breeds)
    },
  })

  const { submitPetAttempt } = useSubmitPetAttempt()

  const [isPuppy, setIsPuppy] = useState(false)

  const [loading, setLoading] = useState(true)
  const [step2, setStep2] = useState<
    Omit<PetAttemptStep2, 'weight'> & {
      mixedBreed: boolean
      weight?: number | null
      adultWeight?: number | null
    }
  >({
    breed: 'UNKNOWN',
    mixedBreed: false,
    bodyCondition: BodyConditions.IDEAL,
    activityLevel: ActivityLevels.MODERATELY_ACTIVE,
    weightChangeDesire: PetWeightChangeDesire.STAY,
    isPuppy: undefined,
    weight: null,
  })

  const [breeds, setBreeds] = useState<{ name: string }[]>([])
  const [breedQuery, setBreedQuery] = useState('')

  const avgAdultSizeWeights = {
    [PetAdultSizes.XSMALL]: 10,
    [PetAdultSizes.SMALL]: 22.5,
    [PetAdultSizes.MEDIUM]: 45,
    [PetAdultSizes.LARGE]: 80,
    [PetAdultSizes.XLARGE]: 105,
  }

  const adultSizeFulfilled = !isPuppy || step2.adultSize !== undefined

  const allFieldsFilled =
    (step2.breed || step2.mixedBreed) &&
    step2.bodyCondition &&
    step2.activityLevel &&
    step2.weight &&
    adultSizeFulfilled

  useEffect(() => {
    if (status === 'loading') return
    setLoading(false)

    if (!petAttemptStep2) return

    const date = new Date(format(new Date(petAttemptStep2.birthDate), 'yyyy-MM-dd'))
    const age = differenceInMonths(new Date(), date)

    const isOverPuppyAgeLimit = age >= 22

    setIsPuppy(!isOverPuppyAgeLimit)

    setStep2({
      ...petAttemptStep2,
      weight: ozToLb(petAttemptStep2.weight),
      mixedBreed: !petAttemptStep2.breed && !!petAttemptStep2.size,
    })
  }, [status, petAttemptStep2])

  useEffect(() => {
    if (!breedQuery) {
      setBreeds(petBreeds)
    } else {
      setBreeds(
        petBreeds.filter((breed) =>
          breed.name.replace(/_/g, ' ').toLowerCase().includes(breedQuery.toLowerCase())
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breedQuery])

  const petName = petAttemptStep2?.name || 'your pet'

  if (loading) return <Loader />

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'weight') {
      const weight = parseFloat(e.target.value)
      setStep2((prevStep2) => ({
        ...prevStep2,
        weight,
      }))
    } else {
      setStep2((prevStep2) => ({
        ...prevStep2,
        [e.target.name]: e.target.type === 'number' ? parseFloat(e.target.value) : e.target.value,
      }))
    }
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const petAttemptRequest = {
      ...step2,
      weight: lbToOz(step2.weight as number),
      breed: step2.mixedBreed ? 'UNKNOWN' : step2.breed.replace(/ /g, '_').toUpperCase(),
      mixedBreed: undefined,
      adultWeight: step2.adultSize ? lbToOz(avgAdultSizeWeights[step2.adultSize]) : undefined,
    }

    const updatedPetAttemptRequest = determineFeedingProductType(petAttemptRequest)

    submitPetAttempt(
      {
        ...updatedPetAttemptRequest,
        ...(petAttemptStep2?.mealPlan && {
          mealPlan: { ...petAttemptStep2.mealPlan, rawDietPercentage: 100 },
        }),
        utmCampaign: Object.keys(router.query).length > 0 ? router.query : undefined,
      } as PetAttemptStep2 & Pick<PetAttempt, 'utmCampaign'>,
      {
        onSuccess: () => {
          router.replace('/meal-plan/step-3')
        },
        onError: (error) => {
          console.log(error.message)
          addToast({
            title: 'Oopsie!',
            message: 'Please select a valid breed for your dog, or select mixed breed.',
            duration: 5000,
            tone: 'error',
            id: 'dog-breed-error',
            onClose: () => removeToast('dog-breed-error'),
          })
        },
      }
    )
  }

  return (
    <MealPlanLayout step={2}>
      <Step2Root>
        <MealPlanQuestionnaireCard>
          <MealPlanFormContainer>
            <form onSubmit={onSubmit}>
              <Stack direction="column" gap="6" alignItems="center" css={{ width: '100%' }}>
                <Stack direction="column" gap="7" alignItems="center" css={{ maxWidth: '492px' }}>
                  <Text
                    align="center"
                    weight="semiBold"
                    size={{ '@initial': 'h3', '@bp2': 'h1' }}
                    font="display"
                    as="h1"
                    color="accent-black"
                  >
                    {t('meal-plan:step-2.step-name')}
                  </Text>
                  <Stack gap="6" direction="column">
                    <Stack direction="column" gap="4">
                      <Stack direction="column" gap="1">
                        <Autocomplete
                          displayLabel={false}
                          outerLabel={InputLabel}
                          placeholder="Select an option"
                          label={t('meal-plan:step-2.breed', { petName })}
                          items={breeds}
                          onChange={(query) => {
                            setBreedQuery(query)
                            query && setStep2({ ...step2, breed: query })
                          }}
                          itemToLabel={(breed) =>
                            breed ? capitalize(breed.name.replace(/_/g, ' ')) : ''
                          }
                          onItemSelected={(breed) =>
                            breed && setStep2({ ...step2, breed: breed.name })
                          }
                          optionFormatter={(breed) =>
                            breed && capitalize(breed.name.replace(/_/g, ' '))
                          }
                          defaultValue={
                            step2.mixedBreed
                              ? { name: 'Unknown' }
                              : step2.breed && { name: step2.breed }
                          }
                          disabled={step2.mixedBreed}
                          aria-labelledby="breed"
                          css={{ alignSelf: 'center' }}
                        />
                        <Stack direction="column" gap="2" alignItems="flex-start">
                          <Checkbox
                            label={t('meal-plan:step-2.mixed-breed', { petName })}
                            defaultChecked={step2.mixedBreed}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              setStep2({ ...step2, mixedBreed: e.target.checked })
                            }
                            customCSS={{ fontSize: '$body', width: '100%' }}
                          />
                        </Stack>
                      </Stack>
                    </Stack>
                    <TextInput
                      outerLabel={(props) => (
                        <InputLabel {...props}>
                          {t('meal-plan:step-2.weight', { petName })}
                        </InputLabel>
                      )}
                      label={t('meal-plan:step-2.weight-label')}
                      name="weight"
                      type="number"
                      step="1"
                      defaultValue={
                        (step2.weight as number) && step2?.weight && step2.weight > 0
                          ? step2.weight
                          : ''
                      }
                      placeholder={t('meal-plan:step-2.weight-label')}
                      suffix={
                        <Text color="accent-black" weight="semiBold">
                          Lb
                        </Text>
                      }
                      onChange={onChange}
                    />
                    <div
                      style={{
                        display: isPuppy && step2?.weight && step2.weight > 0 ? 'block' : 'none',
                      }}
                    >
                      <Stack direction="column" gap="2" alignItems="center">
                        <Stack direction="column" gap="2">
                          <RadioGroup.Root
                            pill
                            name="adultWeight"
                            label={t('meal-plan:step-2.adult-weight', { petName })}
                            defaultValue={step2.adultSize}
                            onValueChange={(value) =>
                              setStep2({
                                ...step2,
                                adultSize: value as PetAdultSizes,
                                adultWeight: lbToOz(avgAdultSizeWeights[value as PetAdultSizes]),
                              })
                            }
                          >
                            <RadioOptionsWrapper>
                              {(step2.adultSize === PetAdultSizes.XSMALL ||
                                (step2?.weight && step2.weight < 15)) && (
                                <TextRadioOption value={PetAdultSizes.XSMALL} label="Under 15lbs" />
                              )}
                              {(step2.adultSize === PetAdultSizes.SMALL ||
                                (step2?.weight && step2.weight < 30)) && (
                                <TextRadioOption value={PetAdultSizes.SMALL} label="15-30lbs" />
                              )}
                              {(step2.adultSize === PetAdultSizes.MEDIUM ||
                                (step2?.weight && step2.weight < 60)) && (
                                <TextRadioOption value={PetAdultSizes.MEDIUM} label="30-60lbs" />
                              )}
                              {(step2.adultSize === PetAdultSizes.LARGE ||
                                (step2?.weight && step2.weight < 100)) && (
                                <TextRadioOption value={PetAdultSizes.LARGE} label="60-100lbs" />
                              )}
                              {(step2.adultSize === PetAdultSizes.XLARGE || step2?.weight) && (
                                <TextRadioOption value={PetAdultSizes.XLARGE} label="100lb +" />
                              )}
                            </RadioOptionsWrapper>
                          </RadioGroup.Root>
                        </Stack>
                      </Stack>
                    </div>
                    <RadioGroupContainer css={{ width: '100%' }}>
                      <RadioGroup.Root
                        className="body-condition"
                        name="bodyCondition"
                        direction="column"
                        label={t('meal-plan:step-2.body-condition', { petName })}
                        defaultValue={step2.bodyCondition}
                        onValueChange={(bodyCondition) =>
                          setStep2({
                            ...step2,
                            bodyCondition: bodyCondition as BodyConditions,
                            weightChangeDesire: PetWeightChangeDesire.STAY,
                          })
                        }
                      >
                        <Stack width="fullWidth" direction="column" gap="3">
                          <TextRadioOption
                            value={BodyConditions.UNDERWEIGHT}
                            label={t('meal-plan:step-2.underweight')}
                            subtext={t('meal-plan:step-2.underweight-help-text')}
                          />
                          <TextRadioOption
                            value={BodyConditions.SKINNY}
                            label={t('meal-plan:step-2.skinny')}
                            subtext={t('meal-plan:step-2.skinny-help-text')}
                          />

                          <TextRadioOption
                            value={BodyConditions.IDEAL}
                            label={t('meal-plan:step-2.ideal')}
                            subtext={t('meal-plan:step-2.ideal-help-text')}
                          />

                          <TextRadioOption
                            value={BodyConditions.CHUBBY}
                            label={t('meal-plan:step-2.chubby')}
                            subtext={t('meal-plan:step-2.chubby-help-text')}
                          />

                          <TextRadioOption
                            value={BodyConditions.OVERWEIGHT}
                            label={t('meal-plan:step-2.overweight')}
                            subtext={t('meal-plan:step-2.overweight-help-text')}
                          />
                        </Stack>
                      </RadioGroup.Root>
                    </RadioGroupContainer>
                  </Stack>
                </Stack>
                <LoseGainContainer>
                  {[BodyConditions.OVERWEIGHT, BodyConditions.CHUBBY].includes(
                    step2.bodyCondition
                  ) && (
                    <RadioGroup.Root
                      defaultValue={
                        step2.weightChangeDesire === PetWeightChangeDesire.LOSE ? '1b' : '0b'
                      }
                      name="loseWeight"
                      direction="column"
                      label={t('meal-plan:step-2.lose-weight', { petName })}
                      onValueChange={(value: string) =>
                        setStep2({
                          ...step2,
                          weightChangeDesire:
                            value === '1b'
                              ? PetWeightChangeDesire.LOSE
                              : PetWeightChangeDesire.STAY,
                        })
                      }
                      value={step2.weightChangeDesire === PetWeightChangeDesire.LOSE ? '1b' : '0b'}
                    >
                      <Stack direction="column" gap="3" width="fullWidth">
                        <TextRadioOption value={'1b'} label={t(`common:yes`)} />
                        <TextRadioOption value={'0b'} label={t(`common:no`)} />
                      </Stack>
                    </RadioGroup.Root>
                  )}

                  {[BodyConditions.UNDERWEIGHT, BodyConditions.SKINNY].includes(
                    step2.bodyCondition
                  ) && (
                    <RadioGroup.Root
                      label={t('meal-plan:step-2.gain-weight', { petName })}
                      name="gainWeight"
                      defaultValue={
                        step2.weightChangeDesire === PetWeightChangeDesire.GAIN ? '1a' : '0a'
                      }
                      onValueChange={(value: string) =>
                        setStep2({
                          ...step2,
                          weightChangeDesire:
                            value === '1a'
                              ? PetWeightChangeDesire.GAIN
                              : PetWeightChangeDesire.STAY,
                        })
                      }
                      direction="column"
                    >
                      <Stack direction="column" width="fullWidth" gap="3">
                        <TextRadioOption value={'1a'} label={t(`common:yes`)} />
                        <TextRadioOption value={'0a'} label={t(`common:no`)} />
                      </Stack>
                    </RadioGroup.Root>
                  )}
                </LoseGainContainer>

                <RadioGroupContainer>
                  <RadioGroup.Root
                    name="activityLevel"
                    direction="column"
                    label={t('meal-plan:step-2.activity-level', { petName })}
                    defaultValue={step2.activityLevel}
                    onValueChange={(activityLevel) =>
                      setStep2({ ...step2, activityLevel: activityLevel as ActivityLevels })
                    }
                  >
                    <Stack direction="column" gap="3">
                      <TextRadioOption
                        value={ActivityLevels.INACTIVE}
                        label={t('meal-plan:step-2.leisurely')}
                        subtext={t('meal-plan:step-2.leisurely-helper-text')}
                      />
                      <TextRadioOption
                        value={ActivityLevels.MODERATELY_ACTIVE}
                        label={t('meal-plan:step-2.moderately-active')}
                        subtext={t('meal-plan:step-2.moderately-active-helper-text')}
                      />
                      <TextRadioOption
                        value={ActivityLevels.ACTIVE}
                        label={t('meal-plan:step-2.active')}
                        subtext={t('meal-plan:step-2.active-helper-text')}
                      />

                      <TextRadioOption
                        value={ActivityLevels.VERY_ACTIVE}
                        label={t('meal-plan:step-2.very-active')}
                        subtext={t('meal-plan:step-2.very-active-helper-text')}
                      />
                    </Stack>
                  </RadioGroup.Root>
                </RadioGroupContainer>
              </Stack>

              <CTAContainer direction="column" alignItems="center" gap="4">
                <Button fullWidth size="regular" disabled={!allFieldsFilled}>
                  {t('common:continue')}
                </Button>
                <Link href="/meal-plan/step-1" passHref>
                  <Text as="a" weight={{ '@initial': 'bold', '@bp1': 'regular' }}>
                    {t('common:go-back')}
                  </Text>
                </Link>
              </CTAContainer>
            </form>
          </MealPlanFormContainer>
        </MealPlanQuestionnaireCard>
      </Step2Root>
      <FooterMessage
        imageAlt="meal-plan:step-2.footer.imageAlt"
        imageSrc={happyDog}
        title="meal-plan:step-2.footer.title"
        subtext="meal-plan:step-2.footer.subtext"
      />
    </MealPlanLayout>
  )
}

Step2.displayName = 'Step2'
